import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  Building2, Copy, DollarSign, Download, FileSpreadsheet, FileText,
  Loader2, LogOut, Users, Wallet, Plus
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { useAuth } from "../../App";
import apiClient from "../../lib/api";
import { toastApiError } from "../../lib/toastApiError";
import LanguageSwitcher from "../../components/LanguageSwitcher";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_97109381-3560-4331-a0e4-28136ead7f0e/artifacts/z6ijff3c_logo1.png";

const KamDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation("nav");
  const { t: ta } = useTranslation("auth");
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [pharmacies, setPharmacies] = useState([]);
  const [commissions, setCommissions] = useState([]);
  const [commissionTotals, setCommissionTotals] = useState(null);
  const [onboarding, setOnboarding] = useState(false);
  const [onboardForm, setOnboardForm] = useState({
    email: "", phone: "", password: "", entity_name: "", entity_type: "pharmacie",
    address: "", city: "", commune: "",
  });
  const [photo, setPhoto] = useState(null);
  const [identityDoc, setIdentityDoc] = useState(null);

  const load = async () => {
    try {
      const [dashRes, phRes, comRes] = await Promise.all([
        apiClient.get("/kam/dashboard"),
        apiClient.get("/kam/pharmacies"),
        apiClient.get("/kam/commissions"),
      ]);
      setDashboard(dashRes.data);
      setPharmacies(phRes.data.pharmacies || []);
      setCommissions(comRes.data.commissions || []);
      setCommissionTotals(comRes.data.totals || null);
    } catch (error) {
      toastApiError(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const downloadExport = async (format, label) => {
    try {
      const res = await apiClient.get(`/kam/commissions/export.${format}`, { responseType: "blob" });
      const blob = new Blob([res.data]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `kam-commissions.${format}`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success(t("kam.exportDownloaded", { format: label }));
    } catch (error) {
      toastApiError(error);
    }
  };

  const copyCode = () => {
    const code = dashboard?.kam?.referral_code;
    if (!code) return;
    navigator.clipboard.writeText(code);
    toast.success(t("kam.codeCopied"));
  };

  const handleOnboard = async (e) => {
    e.preventDefault();
    if (!photo || !identityDoc) {
      toast.error(t("kam.photoIdentityRequired"));
      return;
    }
    setOnboarding(true);
    try {
      const fd = new FormData();
      Object.entries(onboardForm).forEach(([k, v]) => fd.append(k, v || ""));
      fd.append("photo", photo);
      fd.append("identity_doc", identityDoc);
      const res = await apiClient.post("/kam/pharmacies/onboard", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(res.data.message || t("kam.onboardSuccess"));
      setOnboardForm({
        email: "", phone: "", password: "", entity_name: "", entity_type: "pharmacie",
        address: "", city: "", commune: "",
      });
      setPhoto(null);
      setIdentityDoc(null);
      load();
    } catch (error) {
      toastApiError(error);
    } finally {
      setOnboarding(false);
    }
  };

  const stats = dashboard?.stats || {};
  const kam = dashboard?.kam || {};

  return (
    <div className="min-h-[100dvh] bg-slate-50">
      <header className="sticky top-0 z-40 bg-white/90 backdrop-blur border-b">
        <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src={LOGO_URL} alt="OMU" className="h-10 w-auto" />
            <span className="font-heading font-semibold hidden sm:inline">{t("kam.title")}</span>
          </Link>
          <div className="flex items-center gap-2">
            <LanguageSwitcher compact />
            <Badge className="bg-omu-blue-500">KAM</Badge>
            <span className="text-sm text-slate-600 hidden sm:inline">{user?.name}</span>
            <Button variant="outline" onClick={() => { logout(); navigate("/login"); }}>
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6 pb-12 w-full">
        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="h-10 w-10 animate-spin text-omu-blue-500" />
          </div>
        ) : (
          <>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-slate-500">{t("kam.referralCode")}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-2">
                    <code className="text-lg font-bold text-omu-blue-600">{kam.referral_code || "—"}</code>
                    <Button size="icon" variant="ghost" onClick={copyCode}><Copy className="h-4 w-4" /></Button>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">{t("kam.referralCodeHint")}</p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-sm font-medium text-slate-500">{t("kam.dueUnpaid")}</CardTitle></CardHeader>
                <CardContent>
                  <p className="text-2xl font-bold text-amber-600">{Number(stats.due_total || 0).toLocaleString()} CDF</p>
                  <p className="text-xs text-slate-500">{stats.due_count || 0} {t("kam.lines")}</p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-sm font-medium text-slate-500">{t("kam.alreadyPaid")}</CardTitle></CardHeader>
                <CardContent>
                  <p className="text-2xl font-bold text-green-600">{Number(stats.paid_total || 0).toLocaleString()} CDF</p>
                  <p className="text-xs text-slate-500">{stats.paid_count || 0} {t("kam.lines")}</p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-sm font-medium text-slate-500">{t("kam.sponsoredPharmacies")}</CardTitle></CardHeader>
                <CardContent>
                  <p className="text-2xl font-bold">{stats.pharmacies_total || 0}</p>
                  <p className="text-xs text-slate-500">{stats.pharmacies_active || 0} {t("kam.active")}</p>
                </CardContent>
              </Card>
            </div>

            <Tabs defaultValue="pharmacies">
              <TabsList>
                <TabsTrigger value="pharmacies"><Building2 className="h-4 w-4 mr-1" />{t("kam.tabPharmacies")}</TabsTrigger>
                <TabsTrigger value="commissions"><Wallet className="h-4 w-4 mr-1" />{t("kam.tabCommissions")}</TabsTrigger>
                <TabsTrigger value="onboard"><Plus className="h-4 w-4 mr-1" />{t("kam.tabOnboard")}</TabsTrigger>
              </TabsList>

              <TabsContent value="pharmacies" className="mt-4">
                <Card>
                  <CardContent className="p-0">
                    {pharmacies.length === 0 ? (
                      <p className="p-6 text-slate-500 text-center">{t("kam.noPharmacies")}</p>
                    ) : (
                      <div className="divide-y">
                        {pharmacies.map((p) => (
                          <div key={p.id} className="p-4 flex flex-wrap items-center justify-between gap-2">
                            <div>
                              <p className="font-medium">{p.entity_name}</p>
                              <p className="text-sm text-slate-500">{[p.commune, p.city].filter(Boolean).join(" · ")}</p>
                            </div>
                            <Badge className={p.status === "active" ? "bg-green-100 text-green-800" : "bg-amber-100 text-amber-800"}>
                              {p.status === "active" ? t("kam.statusActive") : t("kam.statusPending")}
                            </Badge>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="commissions" className="mt-4">
                <div className="flex flex-wrap gap-2 mb-3">
                  <Button variant="outline" size="sm" onClick={() => downloadExport("csv", "CSV")}>
                    <Download className="h-4 w-4 mr-1" /> {t("kam.exportCsv")}
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => downloadExport("xlsx", "Excel")}>
                    <FileSpreadsheet className="h-4 w-4 mr-1" /> {t("kam.exportExcel")}
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => downloadExport("pdf", "PDF")}>
                    <FileText className="h-4 w-4 mr-1" /> {t("kam.exportPdf")}
                  </Button>
                </div>
                <Card>
                  <CardContent className="p-0">
                    {commissions.length === 0 ? (
                      <p className="p-6 text-slate-500 text-center">{t("kam.noCommissions")}</p>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-slate-50 text-left">
                            <tr>
                              <th className="p-3">{t("kam.colPharmacy")}</th>
                              <th className="p-3">{t("kam.colSubscription")}</th>
                              <th className="p-3">{t("kam.colRate")}</th>
                              <th className="p-3">{t("kam.colCommission")}</th>
                              <th className="p-3">{t("kam.colStatus")}</th>
                              <th className="p-3">{t("kam.colDate")}</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y">
                            {commissions.map((c) => (
                              <tr key={c.id}>
                                <td className="p-3">{c.pharmacy_name}</td>
                                <td className="p-3">{Number(c.subscription_amount || 0).toLocaleString()} {c.currency || "CDF"}</td>
                                <td className="p-3">{c.commission_rate}%</td>
                                <td className="p-3 font-medium">{Number(c.commission_amount || 0).toLocaleString()} {c.currency || "CDF"}</td>
                                <td className="p-3">
                                  <Badge className={c.status === "paid" ? "bg-green-100 text-green-800" : "bg-amber-100 text-amber-800"}>
                                    {c.status === "paid" ? t("kam.statusPaid") : t("kam.statusDue")}
                                  </Badge>
                                </td>
                                <td className="p-3 text-slate-500">{c.created_at ? new Date(c.created_at).toLocaleDateString() : "—"}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                    {commissionTotals && (
                      <div className="p-4 border-t bg-slate-50 flex flex-wrap gap-4 text-sm">
                        <span><DollarSign className="inline h-4 w-4" /> {t("kam.totalDue")} : <strong>{Number(commissionTotals.due_total || 0).toLocaleString()} CDF</strong></span>
                        <span>{t("kam.totalPaid")} : <strong>{Number(commissionTotals.paid_total || 0).toLocaleString()} CDF</strong></span>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="onboard" className="mt-4">
                <Card>
                  <CardHeader>
                    <CardTitle className="font-heading flex items-center gap-2">
                      <Users className="h-5 w-5" /> {t("kam.onboardTitle")}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <form onSubmit={handleOnboard} className="grid sm:grid-cols-2 gap-4 form-scroll-section pb-2">
                      <div><Label>{ta("email")} *</Label><Input required value={onboardForm.email} onChange={(e) => setOnboardForm({ ...onboardForm, email: e.target.value })} /></div>
                      <div><Label>{ta("phone")}</Label><Input value={onboardForm.phone} onChange={(e) => setOnboardForm({ ...onboardForm, phone: e.target.value })} /></div>
                      <div><Label>{ta("password")} *</Label><Input type="password" required minLength={6} value={onboardForm.password} onChange={(e) => setOnboardForm({ ...onboardForm, password: e.target.value })} /></div>
                      <div><Label>{ta("entityName")}</Label><Input required value={onboardForm.entity_name} onChange={(e) => setOnboardForm({ ...onboardForm, entity_name: e.target.value })} /></div>
                      <div>
                        <Label>{ta("entityType")}</Label>
                        <Select value={onboardForm.entity_type} onValueChange={(v) => setOnboardForm({ ...onboardForm, entity_type: v })}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="pharmacie">{ta("entityTypePharmacy")}</SelectItem>
                            <SelectItem value="clinique">{ta("entityTypeClinic")}</SelectItem>
                            <SelectItem value="cabinet">{ta("entityTypeCabinet")}</SelectItem>
                            <SelectItem value="autre">{ta("entityTypeOther")}</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div><Label>{ta("cityOptional")}</Label><Input value={onboardForm.city} onChange={(e) => setOnboardForm({ ...onboardForm, city: e.target.value })} /></div>
                      <div className="sm:col-span-2"><Label>{ta("address")}</Label><Input required value={onboardForm.address} onChange={(e) => setOnboardForm({ ...onboardForm, address: e.target.value })} /></div>
                      <div><Label>{ta("commune")}</Label><Input value={onboardForm.commune} onChange={(e) => setOnboardForm({ ...onboardForm, commune: e.target.value })} /></div>
                      <div><Label>{ta("yourPhoto")}</Label><Input type="file" accept="image/*" required onChange={(e) => setPhoto(e.target.files?.[0] || null)} /></div>
                      <div><Label>{ta("identityDoc")}</Label><Input type="file" accept="image/*" required onChange={(e) => setIdentityDoc(e.target.files?.[0] || null)} /></div>
                      <div className="sm:col-span-2">
                        <Button type="submit" disabled={onboarding} className="bg-omu-blue-500 hover:bg-omu-blue-600">
                          {onboarding ? <Loader2 className="h-4 w-4 animate-spin" /> : t("kam.createSponsoredPharmacy")}
                        </Button>
                      </div>
                    </form>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </>
        )}
      </main>
    </div>
  );
};

export default KamDashboard;
